import { useState } from 'react'
import { useOnboardingAnalytics } from '@/components/onboarding/useOnboardingAnalytics'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { api } from '@/util/api'
import { useAuth } from '@/state/AuthContext'
import { Button, Form, Notice, TextField } from '@/components/play'
import { PublicAuthLayout } from '@/components/public/PublicAuthLayout'
import { SocialSignIn } from '@/components/onboarding/SocialSignIn'
import {
  authLink,
  safeReturnTo,
} from '@/components/onboarding/authContinuation'

export function SignUpPage() {
  const navigate = useNavigate()
  const track = useOnboardingAnalytics()
  const [searchParams] = useSearchParams()
  const { completeOAuthLogin, isAuthenticated, authReady } = useAuth()
  const returnTo = safeReturnTo(
    searchParams.get('return_to') || searchParams.get('redirect'),
    '/app?create=1',
  )
  const [firstName, setFirstName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (isAuthenticated && authReady) return <Navigate to={returnTo} replace />

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (loading) return
    setLoading(true)
    setError(null)
    try {
      const response = await api.post<{ data: { token: string } }>(
        '/auth/signup',
        {
          user: { email: email.trim(), password, first_name: firstName.trim() },
          ...(searchParams.get('invite_code')
            ? { invite_code: searchParams.get('invite_code') }
            : {}),
        },
      )
      track('signup_completed', { method: 'email' })
      completeOAuthLogin(response.data.token)
      navigate(returnTo, { replace: true })
    } catch (err: any) {
      const apiError = err?.response?.data?.error
      const details = apiError?.errors
      setError(
        details && typeof details === 'object'
          ? Object.values(details).flat().join('\n')
          : apiError?.message ||
              "We couldn't create your account. Try again.",
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <PublicAuthLayout
      eyebrow="Create your account"
      title="Your event starts here."
      description="Bring your cameras together. Share the race as it happens."
      backLink={{ to: '/', label: 'StreamBridge' }}
    >
      {/* A Play Form: a missing or short entry is said beside its field, with focus there, not in the browser's bubble. */}
      <Form onSubmit={handleSubmit}>
        {error && (
          <Notice tone="error" alert>
            {error}
          </Notice>
        )}
        <TextField
          label="Your name"
          optional
          id="first-name"
          autoComplete="given-name"
          value={firstName}
          onChange={(event) => setFirstName(event.target.value)}
          disabled={loading}
        />
        <TextField
          label="Email"
          id="email"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
          requiredMessage="Enter your email address."
          disabled={loading}
        />
        <TextField
          label="Password"
          description="At least 8 characters."
          id="password"
          type="password"
          autoComplete="new-password"
          minLength={8}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          required
          requiredMessage="Choose a password."
          validate={(value) => (String(value ?? '').length < 8 ? 'Use at least 8 characters.' : null)}
          disabled={loading}
        />
        <Button type="submit" className="w-full" loading={loading}>
          {loading ? 'Creating account' : 'Create account'}
          <ArrowRight aria-hidden="true" />
        </Button>
        <SocialSignIn returnTo={returnTo} disabled={loading} />
        <p className="text-center text-sm text-neutral-7">
          Already here?{' '}
          <Link
            to={authLink('/login', returnTo)}
            className="font-semibold text-neutral-10 underline underline-offset-4"
          >
            Sign in
          </Link>
        </p>
      </Form>
    </PublicAuthLayout>
  )
}
